// Report-param grammar for the max-diff family, ported from the coffee block and the controls
// strip above it.
//
// Four params carry the block's state, addressed by prefix:
//   md   which measure the items are read in
//   cm   the chart's mode -- or the marker saying the word cloud is showing instead of a chart
//   z    the chart's zoom (shared with the other views of the question)
//   s    the sort order (shared with the other views of the question)
//
// `cm` is overloaded on purpose, the way the coffee block left it: a question showing its items as
// a word cloud has no chart mode to remember, so the one param says which of the two is on screen.
// `off` is the marker for a question that offers the cloud by default and was turned back.

import { getDefaultViewMode, getViewModeList } from '@aytm/app/stats/page/question-views/md/lib/controls-utils'
import {
  DEFAULT_SORT_ORDER,
  decodeSortParam,
  encodeSortParam
} from '@aytm/app/stats/page/question-views/components/lib/sort-param'
import { WC_CHART_MODE, isWcChartMode } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

export { DEFAULT_SORT_ORDER }

export { WC_CHART_MODE, isWcChartMode }

export const resolveSortOrder = (section, defaultSortOrder = DEFAULT_SORT_ORDER) =>
  decodeSortParam(section, { defaultSortOrder })

export const calcSortParamsSection = (sortOrder, defaultSortOrder = DEFAULT_SORT_ORDER) => ({
  s: encodeSortParam(sortOrder, { defaultSortOrder })
})

// The measures the question offers. Which they are never depends on the question -- how many items
// a respondent saw per screen only names the first one.
const viewModeValues = itemsPerSet => getViewModeList(itemsPerSet).map(mode => mode.value)

// A stored measure the question no longer offers is not worth honouring, so it falls back the same
// way silence does.
export const resolveViewMode = ({ md } = {}, { format, answer, itemsPerSet } = {}) => {
  const defaultViewMode = getDefaultViewMode(format, answer)

  if (null == md || !viewModeValues(itemsPerSet).includes(md)) return defaultViewMode

  return md
}

export const calcViewModeSection = (viewMode, defaultViewMode) => ({
  md: (viewMode !== defaultViewMode && viewMode) || null
})

// The chart does not survive the measure being changed under it: the mode it settled on was settled
// for the measure being left behind, and utility scores are signed, so a zoom picked against a
// probability axis means nothing on theirs. Both are reset as the measure is picked rather than
// derived from it -- derived, they would keep wiping a zoom the researcher set afterwards.
export const calcViewModeReset = viewMode => ({
  chartMode: null,
  ...('raw' === viewMode ? { zoomLevel: null } : {})
})

// A zoom that says what the report already says is not worth storing, so the question keeps
// following the report-wide one instead of pinning a copy of it.
export { calcZoomSection } from '@aytm/app/stats/page/question-views/components/lib/zoom-param'

// A question that offers the cloud by default shows it until the researcher says otherwise, and
// `off` is how saying otherwise is written down. The default is settled here, as the block opens,
// so that from then on the mode says outright which of the two is up -- left to be worked out on
// every read, going back to the chart would read as the default all over again.
export const resolveChartMode = ({ cm } = {}, format = {}) => {
  if (isWcChartMode(cm)) return WC_CHART_MODE
  if (null == cm && isWcChartMode(format?.chartMode)) return WC_CHART_MODE

  return cm || null
}

export const isWordCloud = chartMode => isWcChartMode(chartMode)

// The chart mode the question declares, which the chart falls back to before the report's. The
// cloud marker is not one: a question declaring it has no chart mode of its own to fall back to.
export const calcModelChartMode = (chartMode, format = {}) => {
  if (isWcChartMode(chartMode)) return WC_CHART_MODE
  if (isWcChartMode(format?.chartMode)) return null

  return format?.chartMode || null
}

// A chart mode that says what the question already says is not worth storing -- except when the
// question says "cloud" and the researcher wants a chart, which is what there is no silence for.
//
// What the question says is read off its format alone: the model attribute the coffee also
// consulted could only ever hold the cloud marker this call is deciding about.
export const calcChartParamsSection = ({ chartMode, format } = {}) => {
  const stored = format?.chartMode || 'off'

  if (isWcChartMode(stored) && !isWcChartMode(chartMode)) return { cm: 'off' }

  return { cm: chartMode === stored ? null : chartMode || null }
}
